export const useUserValidations = () => {
  const {
    userAddressZipcode,
    userCountry,
    userAddressFullAddress,
    userAddressCity,
    userAddressState,
    userHasDocument,
    userHasBirthdate,
    userPhone,
    userPhoneDdi,
    userHasStrongPassword,
  } = useUserData();

  const { user } = storeToRefs(useUserDataStore());
  const { setLastDateSmsSent, setLastDateEmailSent } = useUserActivityStore();

  const { lastDateSmsSent, lastDateEmailSent } = storeToRefs(
    useUserActivityStore(),
  );

  const gpsLatitudeValue = computed<number>(() => {
    return user.value?.userInfo?.latitude || 0;
  });

  const gpsLongitudeValue = computed<number>(() => {
    return user.value?.userInfo?.longitude || 0;
  });

  const gpsLocationCompleted = computed<boolean>(() => {
    return !!gpsLatitudeValue.value && !!gpsLongitudeValue.value;
  });

  const gpsLastLocationAt = computed<string>(() => {
    return user.value?.userInfo?.location_at || '';
  });

  const logginAttemptsCount = computed<number>(() => {
    return user.value?.userInfo?.login_attempt_qty || 0;
  });

  const isNeedToValidateEmail = computed<boolean>(() => {
    return !!user.value?.userInfo?.seven_days_since_last_login;
  });

  const isEmailVerified = computed(() => {
    return !!user.value?.userInfo?.validate_email_at;
  });

  const isSmsVerified = computed(() => {
    return !!user.value?.userInfo?.validate_sms_at;
  });

  const isPhoneVerifiedOrComplete = computed<boolean>(() => {
    const { isSmsIntegrationEnabled } = useFeatureFlagsJson();

    const isPhoneVerified = ref<boolean>(false);

    if (!isSmsIntegrationEnabled.value) {
      isPhoneVerified.value = !!userPhone.value && !!userPhoneDdi.value;
    } else {
      isPhoneVerified.value = !!isSmsVerified.value;
    }

    return !!isPhoneVerified.value;
  });

  const isAddressComplete = computed(() => {
    return (
      !!userAddressZipcode.value &&
      !!userCountry.value &&
      !!userAddressFullAddress.value &&
      !!userAddressCity.value &&
      !!userAddressState.value &&
      userAddressZipcode.value.length > 5
    );
  });

  const isDocumentComplete = computed<boolean>(() => {
    const { isKycIntegrationEnabled } = useKyc();

    const isKycApproved = ref<boolean>(false);

    if (isKycIntegrationEnabled.value) {
      const { isApproved } = useKyc();

      isKycApproved.value = isApproved.value;
    } else {
      isKycApproved.value = true;
    }

    return (
      userHasDocument.value && userHasBirthdate.value && !!isKycApproved.value
    );
  });

  const isUserCompleteRegistration = computed<boolean>(() => {
    return (
      !!isEmailVerified.value &&
      isPhoneVerifiedOrComplete.value &&
      isDocumentComplete.value &&
      isAddressComplete.value
    );
  });

  const isVerifiedToWithdraw = computed<boolean>(() => {
    const { isValidationModuleEnabled } = useModulesValidation();

    if (
      !isValidationModuleEnabled('first_withdraw') ||
      isUserCompleteRegistration.value
    ) {
      return true;
    }

    const { isModuleNeededOnFirstWithdraw } = useModulesValidation();

    if (isModuleNeededOnFirstWithdraw('email') && !isEmailVerified.value) {
      return false;
    }

    if (
      isModuleNeededOnFirstWithdraw('phone') &&
      !isPhoneVerifiedOrComplete.value
    ) {
      return false;
    }

    if (isModuleNeededOnFirstWithdraw('address') && !isAddressComplete.value) {
      return false;
    }

    if (isModuleNeededOnFirstWithdraw('kyc') && !isDocumentComplete.value) {
      return false;
    }

    return true;
  });

  const termsAndConditionsAcceptedDate = computed<string>(() => {
    return user.value?.userInfo?.tc_accepted_at || '';
  });

  const privacyPolicyAcceptedDate = computed<string>(() => {
    return user.value?.userInfo?.privacy_accepted_at || '';
  });

  const lgpdAcceptedDate = computed<string>(() => {
    return user.value?.userInfo?.lgpd_accepted_at || '';
  });

  const lawAcceptedDate = computed<string>(() => {
    return user.value?.userInfo?.law_accepted_at || '';
  });

  const migrationAcceptedDate = computed<string>(() => {
    return user.value?.userInfo?.migrate_accepted_at || '';
  });

  const hasAnyContractPending = computed<boolean>(() => {
    const { isUserMigrationEnabled } = useFeatureFlagsNew();

    if (!isUserMigrationEnabled.value) {
      return (
        !termsAndConditionsAcceptedDate.value ||
        !privacyPolicyAcceptedDate.value ||
        !lgpdAcceptedDate.value ||
        !lawAcceptedDate.value
      );
    }

    return (
      !termsAndConditionsAcceptedDate.value ||
      !privacyPolicyAcceptedDate.value ||
      !lgpdAcceptedDate.value ||
      !lawAcceptedDate.value ||
      !migrationAcceptedDate.value
    );
  });

  const isOnBetBrWithoutMigrateAccepted = computed<boolean>(() => {
    if (!isBetBr()) {
      return false;
    }

    const { userRegisteredAt } = useUserData();

    if (!userRegisteredAt.value) {
      return false;
    }

    const verificationDate = ref('2025-01-20');
    const registeredDate = new Date(userRegisteredAt.value);
    const comparisonDate = new Date(verificationDate.value);

    if (registeredDate < comparisonDate) {
      return false;
    }

    const isMigrationAccepted = computed<boolean>(() => {
      return !!migrationAcceptedDate.value;
    });

    return !isMigrationAccepted.value;
  });

  const checkPendingContracts = () => {
    const { isContractsModalEnabled } = useFeatureFlagsJson();
    const { isAuthenticated } = storeToRefs(useAuthStore());

    if (isContractsModalEnabled.value && isAuthenticated.value) {
      const { userCountry } = useUserData();

      if (userCountry.value === 'BRA') {
        const { hasAnyContractPending } = useUserValidations();

        if (hasAnyContractPending.value) {
          const { openModal } = useLayoutStore();

          openModal('contracts');
        }
      }
    }
  };

  const isAccountSettingsComplete = computed<boolean>(() => {
    return (
      isEmailVerified.value &&
      isPhoneVerifiedOrComplete.value &&
      isAddressComplete.value &&
      isDocumentComplete.value &&
      hasAnyContractPending.value
    );
  });

  const isSecuritySettingsComplete = computed<boolean>(() => {
    return userHasStrongPassword.value;
  });

  async function redirectToPendingSettings() {
    const { isWithinMigrationPeriod } = useFeatureFlagsNew();

    if (!isWithinMigrationPeriod.value) return;

    if (!isAccountSettingsComplete.value) {
      await navigateTo(localeLink('/user/account?highlight=true'));
    } else if (!isSecuritySettingsComplete.value) {
      await navigateTo(localeLink('/user/security?highlight=true'));
    }
  }

  return {
    gpsLatitudeValue,
    gpsLongitudeValue,
    gpsLocationCompleted,
    gpsLastLocationAt,
    logginAttemptsCount,
    isNeedToValidateEmail,
    lastDateSmsSent,
    setLastDateSmsSent,
    lastDateEmailSent,
    setLastDateEmailSent,
    isEmailVerified,
    isSmsVerified,
    isPhoneVerifiedOrComplete,
    isAddressComplete,
    isDocumentComplete,
    isUserCompleteRegistration,
    isVerifiedToWithdraw,
    termsAndConditionsAcceptedDate,
    privacyPolicyAcceptedDate,
    lgpdAcceptedDate,
    lawAcceptedDate,
    migrationAcceptedDate,
    hasAnyContractPending,
    isOnBetBrWithoutMigrateAccepted,
    checkPendingContracts,
    redirectToPendingSettings,
  };
};
